<template>
    <div class="midNightTarget-wrapper mnt-refresh"
        :style="rulesPopupVisible ? { overflow: 'hidden', height: '100vh' } : {}">

        <!-- Month switch drives the whole page, not just the day grid -->
        <div class="mnt-seg">
            <button type="button" :class="{ 'is-on': activeTab === 'thisMonth' }"
                @click="handleTabChange('thisMonth')">This Month</button>
            <button type="button" :class="{ 'is-on': activeTab === 'lastMonth' }"
                @click="handleTabChange('lastMonth')">Last Month</button>
        </div>

        <!-- ===== Hero: qualifying days done this month ===== -->
        <section class="mnt-card mnt-hero">
            <div class="mnt-cardHd">
                <h3>{{ isThisMonth ? 'This month' : 'Last month' }}</h3>
                <!-- current_day is only meaningful for the running month; the
                     API sends the same value on lastMonthData, where it means
                     nothing. -->
                <span class="mnt-range mnt-num" v-if="isThisMonth">
                    Day {{ currentDayNumber }} / {{ tt_monthData.last_day_of_month }}
                </span>
            </div>

            <div class="mnt-heroRow">
                <div class="mnt-ring">
                    <svg width="132" height="132" viewBox="0 0 132 132" aria-hidden="true">
                        <circle cx="66" cy="66" r="58" fill="none" stroke="#EFF1F4" stroke-width="13" />
                        <circle cx="66" cy="66" r="58" fill="none"
                            :stroke="monthComplete ? '#2E9E57' : 'url(#mntRing)'" stroke-width="13"
                            stroke-linecap="round" :stroke-dasharray="ringDash" />
                        <defs>
                            <linearGradient id="mntRing" x1="0" y1="0" x2="132" y2="132" gradientUnits="userSpaceOnUse">
                                <stop stop-color="#FFB020" />
                                <stop offset="1" stop-color="#F2820D" />
                            </linearGradient>
                        </defs>
                    </svg>
                    <div class="mnt-ringLabel">
                        <span class="mnt-ringVal mnt-num">
                            {{ completedDays }}<i>/{{ requiredDays }}</i>
                        </span>
                        <span class="mnt-cap">Days done</span>
                    </div>
                </div>

                <div class="mnt-heroFigures">
                    <div>
                        <span class="mnt-cap">Reward</span>
                        <div class="mnt-reward mnt-num" :class="{ 'is-paid': rewardBeans > 0 }">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                <circle cx="12" cy="12" r="9.5" fill="#F7C948" />
                                <circle cx="12" cy="12" r="6.4" fill="none" stroke="#E0A21B" stroke-width="1.5" />
                            </svg>
                            <span>{{ formatNumber(rewardBeans) }}</span>
                        </div>
                    </div>
                    <div class="mnt-rule"></div>
                    <div>
                        <span class="mnt-cap">{{ monthComplete ? 'Status' : 'Days still needed' }}</span>
                        <div class="mnt-daysLeft mnt-num">
                            {{ monthComplete ? 'Target complete' : daysLeft }}
                        </div>
                    </div>
                </div>
            </div>

            <div class="mnt-note" v-if="perDayTotal > 0">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#F2820D" stroke-width="2.2"
                    stroke-linecap="round">
                    <circle cx="12" cy="12" r="9" />
                    <path d="M12 7v5.2l3.2 1.9" />
                </svg>
                <span>{{ perDayLabel }} live streaming a day, between 8:00 PM and 4:00 AM (IST)</span>
            </div>
        </section>

        <!-- ===== Today ===== -->
        <section class="mnt-card" v-if="isThisMonth && perDayTotal > 0">
            <div class="mnt-cardHd">
                <h3>Today</h3>
                <span class="mnt-sub">{{ perDayLabel }} target</span>
            </div>
            <div class="mnt-todayFigures">
                <span class="mnt-todayDone mnt-num" :class="{ 'is-hit': todayComplete }">
                    {{ formatTime(tt_monthData.per_day_complete_time) }}
                </span>
                <span class="mnt-todayOf">of {{ perDayLabel }}</span>
            </div>
            <div class="mnt-bar">
                <div class="mnt-barFill" :class="{ 'is-hit': todayComplete }"
                    :style="{ width: todayPercent + '%' }"></div>
            </div>
            <div class="mnt-todayLeft" v-if="(tt_monthData.per_day_remaining_time || 0) > 0">
                Remaining {{ formatTime(tt_monthData.per_day_remaining_time) }}
            </div>
        </section>

        <!-- ===== Monthly calculator ===== -->
        <section class="mnt-card">
            <div class="mnt-cardHd">
                <h3>Monthly calculator</h3>
                <button type="button" class="mnt-rulesBtn" @click="showRulesPopup">Rules</button>
            </div>

            <!-- lastMonthData.daily_data comes back as [] until the month has
                 data, which used to leave a tall blank panel. -->
            <p class="mnt-empty" v-if="!parsedDailyData.length">
                No time report for {{ isThisMonth ? 'this month' : 'last month' }} yet.
            </p>
            <div class="mnt-days" v-else>
                <div v-for="entry in parsedDailyData" :key="entry.day" class="mnt-day" :class="{
                    'is-hit': entry.hit,
                    'is-today': entry.isToday,
                    'is-missed': entry.missed,
                    'is-upcoming': entry.upcoming
                }">
                    <span class="mnt-dayNo mnt-num">{{ entry.day }}</span>
                    <span class="mnt-dayTime mnt-num">{{ entry.upcoming ? '—' : formatToHHMM(entry.seconds) }}</span>
                </div>
            </div>
        </section>

        <!-- ===== Notice ===== -->
        <section class="mnt-noticeCard">
            <span class="mnt-noticeIcon">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#B9860B" stroke-width="2.1"
                    stroke-linecap="round" stroke-linejoin="round">
                    <circle cx="12" cy="12" r="9" />
                    <path d="M12 11v5.2" />
                    <path d="M12 7.6h.01" />
                </svg>
            </span>
            <p>
                <b>Notice</b>
                If you do not stream dance for 3 hours a day for at least 24 days in the month, or if you take a
                break for 2 consecutive days, you will not be eligible for the monthly reward.
            </p>
        </section>

        <!-- Info Popup Start  -->
        <div class="mode-area" v-show="isPopupOverlayVisible"></div>
        <div id="rulesPopup"
            :class="{ 'popup_box tt_rules_popup': true, 'rule-pop-up': true, 'transform-in': rulesPopupVisible, 'transform-out': !rulesPopupVisible }">
            <!-- A plain cross in a 32px round button, the same shape and
                 target size as the Gold Coin Battle rules popup. The old grey
                 icon asset drew its own circle, so it read as two rings. -->
            <button type="button" class="closePopup rulePopupCloseBtn" aria-label="Close"
                @click.stop="showRulesPopup">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6"
                    stroke-linecap="round">
                    <path d="M6 6l12 12" />
                    <path d="M18 6L6 18" />
                </svg>
            </button>
            <div class="rulePopup-topHD">
                <div class="text-center rp-HD">Rules For Earning</div>
            </div>
            <div class="rule-content">
                <ul class="rule-listWrap">
                    <li>
                        <div>
                            <div class="rule-en">Host must go live daily between <b>8:00 PM - 4:00 AM (IST)</b>.</div>
                            <div class="rule-hi">होस्ट को हर दिन रात <b>8:00 बजे से सुबह 4:00 बजे (IST)</b> के बीच लाइव आना अनिवार्य है।</div>
                        </div>
                    </li>
                    <li>
                        <div>
                            <div class="rule-en">Minimum <b>3 hours live streaming</b> required per day.</div>
                            <div class="rule-hi">हर दिन कम से कम <b>3 घंटे का लाइव स्ट्रीमिंग</b> करना आवश्यक है।</div>
                        </div>
                    </li>
                    <li>
                        <div>
                            <div class="rule-en">Host can take <b>5 days off per month</b> (with prior notice).</div>
                            <div class="rule-hi">होस्ट को <b>हर महीने में 5 दिन की छुट्टी लेने की अनुमति है</b> (पहले से सूचित करना आवश्यक है)।</div>
                        </div>
                    </li>
                    <li>
                        <div>
                            <div class="rule-en">Host must answer <b>all incoming call requests</b> during live sessions.</div>
                            <div class="rule-hi">लाइव सत्र के दौरान होस्ट को <b>सभी इनकमिंग कॉल रिक्वेस्ट का जवाब देना अनिवार्य है।</b></div>
                        </div>
                    </li>
                    <li>
                        <div>
                            <div class="rule-en"><b>Monthly fixed salary:</b> $100 USD, released via standard payout method.</div>
                            <div class="rule-hi"><b>मासिक फिक्स वेतन:</b> $100 USD, जो स्टैण्डर्ड पेआउट विधि से दिया जाएगा।</div>
                        </div>
                    </li>
                    <li>
                        <div>
                            <div class="rule-en">Any <b>coins earned</b> by the host are separate from the monthly salary, and the host can <b>withdraw their coin earnings anytime</b> as per the standard withdrawal policy.</div>
                            <div class="rule-hi">होस्ट द्वारा <b>कमाए गए कॉइन्स</b> मासिक वेतन से अलग होंगे, और <b>होस्ट उन्हें कभी भी मानक निकासी नीति के अनुसार निकाल सकता है।</b></div>
                        </div>
                    </li>
                </ul>
            </div>
        </div>
    </div>
</template>
<script>
import axios from 'axios'
import './MidnightTargetRefresh.css'

const RING_RADIUS = 58;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

export default {
    name: 'MidnightTarget',
    data() {
        return {
            respData: {},
            tt_monthData: {},
            activeTab: 'thisMonth',
            isPopupOverlayVisible: false,
            rulesPopupVisible: false,
        }
    },
    mounted() {
        this.userId = this.$route.params.user_id;
        this.fetchThisWeekData();
    },
    computed: {
        isThisMonth() {
            return this.activeTab === 'thisMonth';
        },

        currentDayNumber() {
            const day = this.tt_monthData?.current_day;
            return day ? day.replace('day_', '') : '';
        },

        // How many days a host has to bank to qualify, straight from the API —
        // it is not the same number for everyone.
        requiredDays() {
            return this.tt_monthData?.max_day || 0;
        },
        completedDays() {
            return this.tt_monthData?.completed_days || 0;
        },
        daysLeft() {
            return Math.max(0, this.requiredDays - this.completedDays);
        },
        monthComplete() {
            return this.requiredDays > 0 && this.completedDays >= this.requiredDays;
        },

        rewardBeans() {
            return this.tt_monthData?.reward_beans || 0;
        },

        perDayTotal() {
            return this.tt_monthData?.per_day_total_time || 0;
        },
        perDayLabel() {
            const hours = this.perDayTotal / 3600;
            const rounded = Number.isInteger(hours) ? hours : parseFloat(hours.toFixed(1));
            return `${rounded} ${rounded === 1 ? 'hr' : 'hrs'}`;
        },

        todayPercent() {
            if (!this.perDayTotal) return 0;
            const done = this.tt_monthData?.per_day_complete_time || 0;
            const pct = Math.min(100, (done * 100) / this.perDayTotal);
            // A sliver of colour so "started but barely" never reads as zero.
            return pct > 0 ? Math.max(pct, 3) : 0;
        },
        todayComplete() {
            return this.todayPercent >= 100;
        },

        ringDash() {
            const pct = this.requiredDays
                ? Math.min(100, (this.completedDays * 100) / this.requiredDays)
                : 0;
            const filled = (pct / 100) * RING_CIRCUMFERENCE;
            return `${filled.toFixed(1)} ${RING_CIRCUMFERENCE.toFixed(1)}`;
        },

        parsedDailyData() {
            const data = this.tt_monthData?.daily_data || {};
            const target = this.perDayTotal;
            const today = this.today;

            return Object.keys(data).map(key => {
                const day = parseInt(key.replace('day_', ''), 10);
                const seconds = data[key] || 0;
                const hit = target > 0 && seconds >= target;
                // Days after today have not happened yet, so they are neither
                // a hit nor a miss — only the running month has upcoming days.
                const upcoming = this.isThisMonth && day > today;
                return {
                    day,
                    seconds,
                    hit,
                    upcoming,
                    isToday: this.isThisMonth && day === today,
                    missed: !hit && !upcoming && !(this.isThisMonth && day === today),
                };
            }).sort((a, b) => a.day - b.day);
        },

        today() {
            return new Date().getDate();
        }
    },
    methods: {

        fetchThisWeekData() {

            const tokenParam = this.$route.params.token;
            const token = tokenParam.split('=')[0];
            const apiUrl = `api/freetarget-weekly`;
            axios.get(apiUrl, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            })

                .then(response => {
                    this.respData = response.data.result || {};
                    this.handleTabChange('thisMonth');
                })
                .catch(error => {
                    console.error("Error Featching This Week Data", error)
                })
                .finally(() => {
                    this.loading = false;
                })
        },

        handleTabChange(tab) {
            this.activeTab = tab;
            if (tab === 'thisMonth') {
                this.tt_monthData = this.respData.currentMonthData || {};
            } else if (tab === 'lastMonth') {
                this.tt_monthData = this.respData.lastMonthData || {};
            }
        },

        formatNumber(value) {
            return (value || 0).toLocaleString('en-IN');
        },

        formatTime(seconds) {
            seconds = seconds || 0;
            const hrs = String(Math.floor(seconds / 3600)).padStart(2, '0');
            const mins = String(Math.floor((seconds % 3600) / 60)).padStart(2, '0');
            const secs = String(seconds % 60).padStart(2, '0');
            return `${hrs}:${mins}:${secs}`;
        },

        formatToHHMM(seconds) {
            seconds = seconds || 0;
            const hrs = String(Math.floor(seconds / 3600)).padStart(2, '0');
            const mins = String(Math.floor((seconds % 3600) / 60)).padStart(2, '0');
            return `${hrs}:${mins}`;
        },

        showRulesPopup() {
            this.rulesPopupVisible = !this.rulesPopupVisible;
            this.isPopupOverlayVisible = !this.isPopupOverlayVisible;
        },
    },
}
</script>
